// 费用类型相关 getters/mutations/actions
import calc from 'calculatorjs'

export const DECL_CARGOES_FEE_TYPES = ['tax', 'frt', 'notIncTax', 'busi']

// 不包税自动判定沿用旧版本地申报金额分段，避免受后端申报金额接口返回时机影响。
const NOT_INC_TAX_DECL_X_POINTS = [0, 1900, 5000, 10000, 20000, 50000]
const NOT_INC_TAX_DECL_Y_POINTS = [0, 1900, 2001, 3001, 4001, 5000]

// 每种费用类型都要用对应的申报方式与税制参数调用申报金额接口。
export const DECL_CARGOES_FEE_TYPE_PARAMS = {
  tax: {
    jyoShpMethod: '1',
    srInclFreq: '1',
    srInclTax: '1',
  },
  frt: {
    jyoShpMethod: '1',
    srInclFreq: '1',
    srInclTax: '0',
  },
  notIncTax: {
    jyoShpMethod: '1',
    srInclFreq: '0',
    srInclTax: '0',
  },
  busi: {
    jyoShpMethod: '81',
    srInclFreq: '0',
    srInclTax: '0',
  },
}

function toPclidsString (pclids) {
  return Array.isArray(pclids) ? pclids.join(',') : String(pclids || '')
}

function getUseFeeType (state, type) {
  return type || state.createForm?.useFeeType || ''
}

function piecewiseLinearInterpolate (x, xPoints, yPoints) {
  if (x <= xPoints[0]) return yPoints[0]
  if (x >= xPoints[xPoints.length - 1]) return yPoints[yPoints.length - 1]
  for (let i = 0; i < xPoints.length - 1; i++) {
    if (x >= xPoints[i] && x < xPoints[i + 1]) {
      const t = (x - xPoints[i]) / (xPoints[i + 1] - xPoints[i])
      return Math.floor(yPoints[i] + t * (yPoints[i + 1] - yPoints[i]))
    }
  }
  return yPoints[yPoints.length - 1]
}

function calculateLegacyDeclAmount ({ useFeeType, totalAmount, jyWeight, createPrepare }) {
  const amount = Number(totalAmount || 0)
  const weight = Number(jyWeight || 0)
  let declAmount = 0
  const useJetfgxReal = createPrepare?.lcmrouteSe?.twcc?.provider === 'jetfgx' && amount <= 5000

  if (useJetfgxReal) {
    declAmount = amount
  } else if (useFeeType === 'tax' || useFeeType === 'frt') {
    if (amount < 2000) {
      declAmount = 800 + Math.ceil(weight % 200)
    } else {
      declAmount = 1000 + Math.ceil(weight % 500)
    }
  } else {
    declAmount = piecewiseLinearInterpolate(amount, NOT_INC_TAX_DECL_X_POINTS, NOT_INC_TAX_DECL_Y_POINTS)
  }

  if (useFeeType === 'busi' && declAmount < 2001) {
    declAmount = 2001 + Math.ceil(weight % 100)
  }

  return Math.floor(declAmount)
}

function getOptionValue (options, key, fallback) {
  return Object.prototype.hasOwnProperty.call(options, key) ? options[key] : fallback
}

function getJyWeightKg (state) {
  // prepare 返回的 jyWeight 单位是 g，包税公式按 kg 计算。
  // return Number(state.createPrepare?.parcelsInfo?.jyWeight || 0) / 1000
  return Number(state.createPrepare?.parcelsInfo?.jyWeight || 0)
}

function canUseTaxByFee (state, getters) {
  if (!getters.declAmountReadyByType('tax')) return false
  const taxDeclAmount = getters.declAmountByType('tax')
  // 全包税预算：每 kg 15 元需大于申报金额 5%。
  return calc.mul(15, getJyWeightKg(state)) > calc.mul(taxDeclAmount, 0.05)
}

function canUseFrtByFee (state, getters) {
  if (!getters.declAmountReadyByType('frt')) return false
  const frtDeclAmount = getters.declAmountByType('frt')
  // 包频繁税预算：每 kg 10 元需大于申报金额 5%。
  return calc.mul(10, getJyWeightKg(state)) > calc.mul(frtDeclAmount, 0.05)
}

function checkCanUseTax (state, getters, options = {}) {
  if (!state.createPrepare) return false

  // 组件会传入按包裹明细算好的限制清单，避免 store 用粗略 jyCargoes 结果覆盖页面判断。
  const tpItemsInclTaxMaxWt = getOptionValue(options, 'tpItemsInclTaxMaxWt', getters.tpItemsInclTaxMaxWt || [])
  const trfTpItems = getOptionValue(options, 'trfTpItems', getters.trfTpItems || [])
  const hasBizParcel = getOptionValue(options, 'hasBizParcel', state.createPrepare.hasBizParcel)
  const transport = getters.shippingRatesInfo?.transport
  const canUseByInclTaxMaxWt = tpItemsInclTaxMaxWt.length === 0
  const canUseByTrfItems = trfTpItems.length === 0
  const canUseByBizParcel = transport !== 'se' || !hasBizParcel
  const canUseByRealAmount = transport !== 'se' || getters.traRealAmtsRMBTotal <= 3000

  return canUseByInclTaxMaxWt &&
    canUseByTrfItems &&
    canUseByBizParcel &&
    canUseByRealAmount &&
    canUseTaxByFee(state, getters)
}

function checkCanUseFrt (state, getters, options = {}) {
  if (!state.createPrepare) return false

  // onlyBusi / onlyNotIncTax 是页面流程限制，由组件传入后与 store 公共条件合并。
  const tpItemsInclTaxMaxWt = getOptionValue(options, 'tpItemsInclTaxMaxWt', getters.tpItemsInclTaxMaxWt || [])
  const onlyBusi = getOptionValue(options, 'onlyBusi', false)
  const onlyNotIncTax = getOptionValue(options, 'onlyNotIncTax', false)
  const isCompanyAddr = getOptionValue(options, 'isCompanyAddr', getters.isCompanyAddr)
  const supportsFrtTax = getOptionValue(options, 'supportsFrtTax', true)
  const canUseByCompanyAddr = !isCompanyAddr
  const canUseByTransport = getters.shippingRatesInfo.transport !== 'air'
  const canUseByInclTaxMaxWt = tpItemsInclTaxMaxWt.length === 0
  const canUseByOnlyBusi = !onlyBusi
  const canUseByOnlyNotIncTax = !onlyNotIncTax
  const canUseByTaxType = !getters.isAllIncTax

  return canUseByCompanyAddr &&
    supportsFrtTax &&
    canUseByTransport &&
    canUseByInclTaxMaxWt &&
    canUseByOnlyBusi &&
    canUseByOnlyNotIncTax &&
    canUseByTaxType &&
    canUseFrtByFee(state, getters)
}

export function buildDeclCargoesLoadingKey (state, getters) {
  if (!state.createForm || !state.createPrepare) return ''
  const shippingRatesInfo = getters.shippingRatesInfo || {}
  // 这些字段变化会影响四种费用类型的申报金额，作为批量计算缓存 key。
  return JSON.stringify({
    pclids: toPclidsString(state.createForm.pclids),
    tradevanAddrid: state.createForm.tradevanAddrid || '',
    jyoWhsAddrid: state.createPrepare.parcelsInfo?.jyAddrid || state.createPrepare.parcels?.[0]?.jyAddrid || '',
    srid: state.createForm.srid || '',
    srTwAddrid: shippingRatesInfo.srTwAddrid || '',
    jyoCarrier: state.createForm.jyoCarrier || '',
  })
}

export function buildCalcDeclCargoesParams (state, getters, type) {
  const form = state.createForm
  const prepare = state.createPrepare
  const shippingRatesInfo = getters.shippingRatesInfo || {}
  const feeTypeParams = DECL_CARGOES_FEE_TYPE_PARAMS[type]
  return {
    pclids: toPclidsString(form?.pclids),
    tradevanAddrid: form?.tradevanAddrid || '',
    jyoWhsAddrid: prepare?.parcelsInfo?.jyAddrid || prepare?.parcels?.[0]?.jyAddrid || '',
    srTwAddrid: shippingRatesInfo.srTwAddrid || '',
    jyoCarrier: form?.jyoCarrier || '',
    jyoShpMethod: feeTypeParams.jyoShpMethod,
    jyoExtraInfo: {
      srInclTax: feeTypeParams.srInclTax,
      srInclFreq: feeTypeParams.srInclFreq,
    },
  }
}

function canCalcDeclCargoes (state, getters) {
  const loadingKey = buildDeclCargoesLoadingKey(state, getters)
  if (!loadingKey) return false
  const parsed = JSON.parse(loadingKey)
  return !!(parsed.pclids && parsed.tradevanAddrid && parsed.jyoWhsAddrid && parsed.srid && parsed.jyoCarrier)
}

export function calcFreezeTaxAmount (declAmount) {
  return Math.ceil(Number(declAmount || 0) * 0.05)
}

export function sumDeclCargoesAmount (declCargoes) {
  const list = Array.isArray(declCargoes) ? declCargoes : []
  return list
    .filter(item => item.customName)
    .reduce((prev, curr) => {
      return calc.add(prev, calc.mul(Number(curr.count || 0), Number(curr.amount || 0)) || 0)
    }, 0)
}

export const getters = {
  // 原始货物价值（台币）
  jyCargoesTotalAmountRaw (state, getters, rootState) {
    if (!state.createPrepare) return 0
    let jyCargoesTotalAmount = state.createPrepare.parcelsInfo.jyCargoes.reduce(
      (prev, curr) => calc.add(prev, curr.totalAmount),
      0
    )
    jyCargoesTotalAmount = calc.mul(jyCargoesTotalAmount, rootState.exchangeRate)
    return Math.round(jyCargoesTotalAmount)
  },

  // 用于计算的基准金额
  totalAmountForCalc (state, getters) {
    const traRealAmtsTWDTotal = getters.traRealAmtsTWDTotal
    // if (window.vueApp.$store.state.isTest) {
    //   return 5000
    // }
    // return 1500
    return traRealAmtsTWDTotal > 0 ? traRealAmtsTWDTotal : getters.jyCargoesTotalAmountRaw
  },

  // 申报金额计算
  declAmount (state, getters) {
    if (!state.createForm) return 0
    return getters.declAmountByType(state.createForm.useFeeType)
  },

  // 申报金额接口是否已返回可用结果
  declAmountReadyByType (state) {
    return (type) => {
      const entry = state.declCargoesByFeeType?.[type]
      return !!entry?.ready
    }
  },

  declAmountsReady (state, getters) {
    return DECL_CARGOES_FEE_TYPES.every(type => getters.declAmountReadyByType(type))
  },

  // 按指定费用类型计算申报金额
  declAmountByType (state) {
    return (type) => {
      const useFeeType = getUseFeeType(state, type)
      if (!useFeeType) return 0
      const entry = state.declCargoesByFeeType?.[useFeeType]
      return entry?.ready ? entry.amount : 0
    }
  },

  legacyDeclAmountByType (state, getters) {
    return (type) => {
      if (!state.createPrepare) return 0
      const useFeeType = getUseFeeType(state, type)
      if (!useFeeType) return 0
      return calculateLegacyDeclAmount({
        useFeeType,
        totalAmount: getters.totalAmountForCalc,
        jyWeight: state.createPrepare.parcelsInfo.jyWeight,
        createPrepare: state.createPrepare,
      })
    }
  },

  // 系統評估價值申報比例
  sysCargoesValRatio (state, getters) {
    const useFeeType = state.createForm?.useFeeType
    if (!getters.declAmountReadyByType(useFeeType)) {
      return state.createForm?.jyoExtraInfo?.cargoesValRatio || '0.00'
    }
    const totalAmount = getters.totalAmountForCalc
    const declAmount = getters.declAmount

    // 计算比例，防止除以0
    const ratio = totalAmount > 0 ? (declAmount / totalAmount) : 1
    return ratio.toFixed(2)
  },

  canUseTaxByOptions (state, getters) {
    return (options = {}) => checkCanUseTax(state, getters, options)
  },

  canUseFrtByOptions (state, getters) {
    return (options = {}) => checkCanUseFrt(state, getters, options)
  },

  // 自动判断的 useFeeType（根据 declAmount 自动判断 X2/X3）
  autoNotIncTaxType (state, getters) {
    // if (getters.hasMutilTwParcel) return 'busi'
    if (!state.createPrepare) return 'notIncTax'

    // declAmount > 2000 → 'busi'(X3), 否则 → 'notIncTax'(X2)
    const notIncTaxDeclAmount = calculateLegacyDeclAmount({
      useFeeType: 'notIncTax',
      totalAmount: getters.totalAmountForCalc,
      jyWeight: state.createPrepare.parcelsInfo.jyWeight,
      createPrepare: state.createPrepare,
    })
    return notIncTaxDeclAmount > 2000 ? 'busi' : 'notIncTax'
  },

  freezeTaxAmount (state, getters) {
    return calcFreezeTaxAmount(getters.declAmount)
  },

  needFreezeAmountByCredit (state, getters) {
    return (credit) => Math.max(0, getters.freezeTaxAmount - Number(credit || 0))
  },

  declCargoesAmount (state) {
    return sumDeclCargoesAmount(state.createForm?.jyoExtraInfo?.declCargoes)
  },

  estimateTaxByDeclCargoes (state, getters) {
    return calcFreezeTaxAmount(getters.declCargoesAmount)
  },

}

export const mutations = {
  setUseFeeType (state, type) {
    if (state.createForm) {
      state.createForm.useFeeType = type
    }
  },
}

export const actions = {
  async refreshDeclCargoesByFeeTypes ({ state, getters, commit, dispatch }, types = DECL_CARGOES_FEE_TYPES) {
    if (!canCalcDeclCargoes(state, getters)) {
      commit('setDeclCargoesLoadingKey', '')
      commit('resetDeclCargoesByFeeType')
      return state.declCargoesByFeeType
    }

    const feeTypes = types.filter(type => DECL_CARGOES_FEE_TYPE_PARAMS[type])
    const loadingKey = buildDeclCargoesLoadingKey(state, getters)
    const isSameReadyCache = state.declCargoesLoadingKey === loadingKey &&
      feeTypes.every(type => state.declCargoesByFeeType?.[type]?.ready)

    if (isSameReadyCache) {
      return state.declCargoesByFeeType
    }

    commit('setDeclCargoesLoadingKey', loadingKey)
    commit('resetDeclCargoesByFeeType')

    await Promise.all(feeTypes.map(async (type) => {
      try {
        const res = await dispatch('calcDeclCargoes', buildCalcDeclCargoesParams(state, getters, type))
        commit('setDeclCargoesByFeeType', {
          type,
          list: res.data.list,
          error: res.data.error,
          loadingKey,
        })
      } catch (e) {
        commit('setDeclCargoesByFeeType', {
          type,
          list: [],
          error: e.message || 'error',
          loadingKey,
        })
      }
    }))

    return state.declCargoesByFeeType
  },

  // 用户选择费用类型
  selectUseFeeType ({ getters, commit }, type) {
    // 如果用户选择的是 notIncTax，需要根据 declAmount 自动判断使用 busi 还是 notIncTax
    if (type === 'notIncTax') {
      // 自动判断：declAmount > 2000 使用 busi，否则使用 notIncTax
      const actualType = getters.autoNotIncTaxType
      commit('setUseFeeType', actualType)
      return actualType
    }

    commit('setUseFeeType', type)
    return type
  },

  // 根据 declAmount 自动更新 useFeeType
  autoUpdateUseFeeType ({ state, getters, commit }) {
    if (!state.createForm) return

    const currentType = state.createForm.useFeeType

    // 只有在不包税模式下才自动切换
    if (currentType === 'notIncTax' || currentType === 'busi') {
      const autoType = getters.autoNotIncTaxType
      if (currentType !== autoType) {
        commit('setUseFeeType', autoType)
      }
    }
  },
}
